/**
 * Calculates the relative luminance of a color according to WCAG 2.0
 * @param {string} color - Color in hex format (#RRGGBB or #RGB)
 * @returns {number} Relative luminance value between 0 and 1
 */
const getRelativeLuminance = (color) => {
  if (!color) return 0;
  
  let hex = color.replace('#', '');
  
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }
  
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const toLinear = (channel) => {
    return channel <= 0.03928 
      ? channel / 12.92 
      : Math.pow((channel + 0.055) / 1.055, 2.4);
  };
  
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

/**
 * Calculates the contrast ratio between two colors according to WCAG 2.0
 * @param {string} color1 - First color in hex format
 * @param {string} color2 - Second color in hex format
 * @returns {number} Contrast ratio between 1 and 21
 */
export const getContrastRatio = (color1, color2) => {
  const lum1 = getRelativeLuminance(color1);
  const lum2 = getRelativeLuminance(color2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Determines the most accessible text color (black or white) for a given background color
 * Uses WCAG 2.0 contrast ratio guidelines (minimum 4.5:1 for normal text)
 * @param {string} backgroundColor - Background color in hex format (#RRGGBB or #RGB)
 * @param {string} [lightColor='#FFFFFF'] - Light text color option
 * @param {string} [darkColor='#000000'] - Dark text color option
 * @returns {string} The most accessible text color (either lightColor or darkColor)
 */
export const getAccessibleTextColor = (backgroundColor, lightColor = '#FFFFFF', darkColor = '#000000') => {
  if (!backgroundColor) return lightColor;
  
  const contrastWithWhite = getContrastRatio(backgroundColor, lightColor);
  const contrastWithBlack = getContrastRatio(backgroundColor, darkColor);
  
  return contrastWithWhite > contrastWithBlack ? lightColor : darkColor;
};

export const mixHexColors = (start, end, amount) => `#${[1, 3, 5].map((offset) => {
  const value = Math.round(parseInt(start.slice(offset, offset + 2), 16) * (1 - amount)
    + parseInt(end.slice(offset, offset + 2), 16) * amount);
  return value.toString(16).padStart(2, '0');
}).join('').toUpperCase()}`;

export const getGivingColorPalette = (baseColor) => {
  if (!/^#[\da-f]{6}$/i.test(baseColor)) return null;
  const base = baseColor.toUpperCase();
  const textColor = getAccessibleTextColor(base);
  let backgroundColor = base;
  let bestContrast = 1;

  for (const target of ['#FFFFFF', '#000000']) {
    for (let step = 1; step <= 20; step++) {
      const candidate = mixHexColors(base, target, step / 20);
      if (getContrastRatio(candidate, textColor) < 4.5) break;
      const contrast = getContrastRatio(base, candidate);
      if (contrast > bestContrast) {
        backgroundColor = candidate;
        bestContrast = contrast;
      }
      if (contrast >= 3) break;
    }
  }

  return {
    backgroundColor,
    surfaceColor: mixHexColors(backgroundColor, base, 0.4),
    textColor,
  };
};

/**
 * Checks if a color combination meets WCAG AA standards
 * @param {string} foreground - Foreground color in hex format
 * @param {string} background - Background color in hex format
 * @param {boolean} [largeText=false] - Whether the text is large (14pt bold or 18pt+)
 * @returns {boolean} True if the combination meets WCAG AA standards
 */
export const meetsWCAGAA = (foreground, background, largeText = false) => {
  const ratio = getContrastRatio(foreground, background);
  return largeText ? ratio >= 3 : ratio >= 4.5;
};

/**
 * Checks if a color combination meets WCAG AAA standards
 * @param {string} foreground - Foreground color in hex format
 * @param {string} background - Background color in hex format
 * @param {boolean} [largeText=false] - Whether the text is large (14pt bold or 18pt+)
 * @returns {boolean} True if the combination meets WCAG AAA standards
 */
export const meetsWCAGAAA = (foreground, background, largeText = false) => {
  const ratio = getContrastRatio(foreground, background);
  return largeText ? ratio >= 4.5 : ratio >= 7;
};

/**
 * Returns a transparent background color based on the given color and opacity
 * @param {string} color - Color in rgb format
 * @param {number} opacity - Opacity value between 0 and 1
 * @returns {string} Transparent background color in rgba format
 */
export const getTransparentBackgroundColor = (color, opacity) => {
    //need this to be for hex value
    if (color.startsWith('#')) {
        const r = parseInt(color.substring(1, 3), 16);
        const g = parseInt(color.substring(3, 5), 16);
        const b = parseInt(color.substring(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}